import { chipTonal } from "@/theme/styleHelpers";
/**
 * WorkbenchSessionPill — current-session chip with a popover that lists
 * every Workbench session for the active project, plus a "+ New session"
 * CTA that opens the Tool picker.
 *
 * Design treatment: chipTonal pill with Tool icon kicker → chevron. On
 * click pops a panel of session rows; each row shows title, Tool chips,
 * last-active relative timestamp, and message count. The active row
 * gets the same chipTonal treatment (so the user can see where they
 * are without having to read titles).
 *
 * The pill stays passive — it emits `onSessionSelect(id)` and
 * `onNewSession()`, and the caller (CopilotRail) handles the actual
 * switch (chatStore.setSessionId + saveLastActiveSession + dialog open).
 */
import { ConfirmDialog } from "@components/ConfirmDialog";
import {
  useDeleteSession,
  useSessions,
} from "@features/history/hooks/useSessions";
import AddIcon from "@mui/icons-material/AddRounded";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import type { ChatSessionSummary } from "@services/schemas";
import { useChatStore } from "@stores/chatStore";
import { useMemo, useRef, useState } from "react";
// `describeTools` was retired with the workbench tool picker (May 2026).
// Session pills no longer render tool chips.

interface WorkbenchSessionPillProps {
  projectId: string;
  organisationId: string | null;
  /** Active session id from chatStore, or null when no session is bound. */
  activeSessionId: string | null;
  /** Live session title from chatStore (overrides the persisted one
   *  while a fresh title is generating on turn 1). */
  activeTitle: string;
  /** Switch to an existing session. */
  onSessionSelect: (sessionId: string) => void;
  /** Open the Tool picker for a new session. */
  onNewSession: () => void;
}

const FALLBACK_TITLE = "Untitled session";
const NO_SESSION_LABEL = "No session";

export function WorkbenchSessionPill({
  projectId,
  organisationId,
  activeSessionId,
  activeTitle,
  onSessionSelect,
  onNewSession,
}: WorkbenchSessionPillProps) {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  const sessionsQuery = useSessions(organisationId, projectId, "workbench");
  const sessions = useMemo(
    () => orderForDisplay(sessionsQuery.data ?? [], activeSessionId),
    [sessionsQuery.data, activeSessionId],
  );

  const activeFromList = activeSessionId
    ? sessions.find((s) => s.session_id === activeSessionId)
    : undefined;
  const labelText = activeSessionId
    ? activeTitle.trim() || activeFromList?.title || FALLBACK_TITLE
    : NO_SESSION_LABEL;

  return (
    <>
      <Box
        component="button"
        ref={anchorRef}
        onClick={() => setOpen((v) => !v)}
        sx={(t) => ({
          ...chipTonal(t),
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          height: 26,
          borderRadius: 999,
          cursor: "pointer",
          font: "inherit",
          fontSize: 12,
          minWidth: 0,
          maxWidth: 220,
        })}
      >
        <ChatBubbleOutlineIcon sx={{ fontSize: 14, flexShrink: 0 }} />
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {labelText}
        </Box>
        <KeyboardArrowDownIcon sx={{ fontSize: 14, flexShrink: 0 }} />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              width: 320,
              maxHeight: 420,
              display: "flex",
              flexDirection: "column",
              border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.18)}`,
            },
          },
        }}
      >
        {/* ── Header ─────────────────────────────────────────────── */}
        <Box sx={{ px: 1.5, pt: 1.25, pb: 0.75 }}>
          <Typography
            variant="overline"
            sx={{
              color: "text.secondary",
              fontFamily: (t) => t.fonts.mono,
              letterSpacing: 1.2,
              lineHeight: 1,
            }}
          >
            Workbench sessions
          </Typography>
        </Box>

        {/* ── Session list ─────────────────────────────────────── */}
        <Box sx={{ flex: 1, overflowY: "auto", pb: 0.5 }}>
          {sessionsQuery.isLoading && (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <CircularProgress size={18} />
            </Box>
          )}
          {!sessionsQuery.isLoading && sessions.length === 0 && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                px: 1.5,
                py: 1.25,
                color: "text.secondary",
              }}
            >
              No sessions yet — start one with the button below.
            </Typography>
          )}
          {sessions.map((s) => (
            <SessionRow
              key={s.session_id}
              session={s}
              active={s.session_id === activeSessionId}
              onClick={() => {
                if (s.session_id !== activeSessionId) {
                  onSessionSelect(s.session_id);
                }
                setOpen(false);
              }}
              activeSessionId={activeSessionId}
            />
          ))}
        </Box>

        <Divider
          sx={{ borderColor: (t) => alpha(t.palette.primary.main, 0.12) }}
        />

        {/* ── Footer ───────────────────────────────────────────── */}
        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              onNewSession();
              setOpen(false);
            }}
            sx={{
              justifyContent: "flex-start",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            New session
          </Button>
        </Box>
      </Popover>
    </>
  );
}

/* ── Session row ──────────────────────────────────────────────── */

function SessionRow({
  session,
  active,
  onClick,
  activeSessionId,
}: {
  session: ChatSessionSummary;
  active: boolean;
  onClick: () => void;
  /** The currently-active session id (chatStore). When the row's
   *  session matches and is deleted, we also clear chatStore so the
   *  rail flips straight to its empty state instead of trying to
   *  hydrate a now-dead session id. */
  activeSessionId: string | null;
}) {
  const subtitle = formatSubtitle(session);
  const deleteSession = useDeleteSession();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const displayTitle = session.title || FALLBACK_TITLE;

  const handleConfirmDelete = () => {
    deleteSession.mutate(session.session_id);
    if (session.session_id === activeSessionId) {
      // Don't leave the rail pointing at a deleted session id —
      // useChat would try to hydrate it and the empty-state /
      // auto-load logic does its own thing on next mount.
      const st = useChatStore.getState();
      st.clearMessages();
    }
    setConfirmDelete(false);
  };

  return (
    <>
      {/* biome-ignore lint/a11y/useSemanticElements: row hosts a nested IconButton for delete; a real <button> would nest buttons (invalid HTML) and we'd lose the row-click target. */}
      <Box
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
        sx={(t) => ({
          position: "relative",
          px: 1.5,
          py: 0.875,
          cursor: "pointer",
          borderLeft: `2px solid ${
            active ? t.palette.primary.main : "transparent"
          }`,
          backgroundColor: active
            ? alpha(t.palette.primary.main, 0.06)
            : "transparent",
          "&:hover": {
            backgroundColor: alpha(t.palette.primary.main, 0.04),
          },
        })}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: active ? 600 : 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayTitle}
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 0.25, color: "text.secondary" }}
            >
              {subtitle}
            </Typography>
          </Box>
          {/* Always-visible delete affordance. Showing it on every
              row (not just hover/active) makes "you can delete any
              session" obvious — the hover-only treatment was too
              subtle and made deletion feel locked behind an active
              state. */}
          <Box sx={{ flexShrink: 0 }}>
            <Tooltip title="Delete session">
              <IconButton
                size="small"
                aria-label={`Delete session ${displayTitle}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDelete(true);
                }}
                sx={{
                  p: "5px",
                  color: "text.disabled",
                  "&:hover": { color: "error.main" },
                }}
              >
                <DeleteIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete session"
        message={`Delete '${displayTitle}'? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete(false)}
        dangerous
      />
    </>
  );
}

/* ── Helpers ──────────────────────────────────────────────────── */

function orderForDisplay(
  sessions: ChatSessionSummary[],
  activeId: string | null,
): ChatSessionSummary[] {
  // Sort by last_active desc; pin the active session to the top so the
  // user always sees their current row before scrolling history.
  const sorted = [...sessions].sort((a, b) => {
    const aT = Date.parse(a.last_active || "") || 0;
    const bT = Date.parse(b.last_active || "") || 0;
    return bT - aT;
  });
  if (!activeId) return sorted;
  const idx = sorted.findIndex((s) => s.session_id === activeId);
  if (idx <= 0) return sorted;
  return [sorted[idx], ...sorted.slice(0, idx), ...sorted.slice(idx + 1)];
}

function formatSubtitle(session: ChatSessionSummary): string {
  const count = session.message_count;
  const ago = relativeTime(session.last_active);
  const msgs = `${count} ${count === 1 ? "message" : "messages"}`;
  return ago ? `${msgs} · ${ago}` : msgs;
}

function relativeTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  const delta = Date.now() - ms;
  const sec = Math.round(delta / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(ms).toLocaleDateString();
}
